import { Field } from '@base-ui/react';
import { ControlledFormDatePicker } from '@components/formInputs/ControlledFormDatePicker';
import { ControlledFormDayRadio } from '@components/formInputs/ControlledFormDayRadio';
import { ControlledFormInput } from '@components/formInputs/ControlledFormInput';
import { ControlledFormTimeRange } from '@components/formInputs/ControlledFormTimeRange';
import { LayoutVariant } from '@components/formInputs/sharedTypes';
import { Switch } from '@components/switch/Switch';
import cx from 'classnames';
import { parseISO } from 'date-fns';
import { useEffect } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import typography from '~styles/typography.scss';
import { AddOrRemoveTables } from './AddOrRemoveTables';
import styles from './ShiftForm.scss';

export interface ShiftFormData {
  startDate: string;
  endDate: string | null;
  repeat: string[];
  name: string;
  startTime: string;
  endTime: string;
  floorPlanTableIds?: string[];
  isRecurring: boolean;
}

export interface ShiftFormProps {
  form: UseFormReturn<ShiftFormData>;
  /** Optional prop to control whether or not recurrence controls are even
   * displayed to the user. Right now this is useful for Shifts that belong to
   * Restaurant Events (which we don't fully support recurrence for) */
  allowRecurrence?: boolean;
  isEditingRecurringShift?: boolean;
  selectTablesEnabled?: boolean;
}

export const ShiftForm = ({
  form,
  allowRecurrence = true,
  isEditingRecurringShift = false,
  selectTablesEnabled = true,
}: ShiftFormProps) => {
  const { control, formState, resetField, setValue, watch } = form;

  const originalDefaultValues = formState.defaultValues;

  const isRecurring = watch('isRecurring');
  const startDate = watch('startDate');

  useEffect(() => {
    // We only want to auto-set the repeat day and clear the end date when the
    // user first toggles on "isRecurring"
    if (isRecurring && !originalDefaultValues?.isRecurring) {
      const repeatDayFromStartDate = parseISO(startDate).getDay();
      setValue('repeat', [repeatDayFromStartDate.toString()]);
      setValue('endDate', null);
    } else {
      resetField('repeat');
      resetField('endDate');
    }
  }, [isRecurring, startDate, setValue]);

  return (
    <>
      <p className={cx(typography.c2, styles.description)}>
        Shifts determine when tables can be reserved.
      </p>
      <form className={styles.form}>
        <ControlledFormInput
          control={control}
          label="Name"
          name="name"
          rules={{
            required: true,
          }}
          type="text"
          variant={LayoutVariant.Vertical}
        />
        <ControlledFormDatePicker
          control={control}
          name="startDate"
          label="Start Date"
          rules={{ required: true }}
        />
        <ControlledFormTimeRange
          control={control}
          groupLabel="Time Frame"
          fromProps={{
            label: 'Start Time',
            name: `startTime`,
            rules: {
              required: true,
            },
          }}
          toProps={{
            label: 'End Time',
            name: `endTime`,
            rules: {
              required: true,
            },
          }}
          variant={LayoutVariant.Vertical}
        />
        {allowRecurrence && (
          <Controller
            control={control}
            name="isRecurring"
            render={({ field: { value, onChange } }) => (
              <Field.Root className={styles.field}>
                <Field.Label>Repeats</Field.Label>
                <Switch
                  disabled={isEditingRecurringShift}
                  checked={value}
                  onCheckedChange={onChange}
                />
              </Field.Root>
            )}
          />
        )}
        {isRecurring && (
          <>
            <ControlledFormDayRadio
              control={control}
              label="Repeats On"
              name="repeat"
              rules={{
                required: true,
              }}
            />
            <ControlledFormDatePicker
              control={control}
              name="endDate"
              label="End Date"
              placeholder="Endless"
              rules={{
                min: {
                  value: startDate,
                  message: 'End Date must be on or after the Start Date.',
                },
              }}
            />
          </>
        )}
      </form>

      {selectTablesEnabled && <AddOrRemoveTables form={form} />}
    </>
  );
};
